<template>
  <div>
    <div class="d-flex justify-content-between">
      <h1 class="page-header">
        {{ $t('school.academic.operation.classOperation.pageTitle') }}
      </h1>
      <ts-breadcrumb :items="[
        { text: $t('toHomePage'), href: '/' },
        {
          text: $t('school.academic.operation.classOperation.pageTitle'),
          href: '/class-operations'
        },
        { text: $t('create'), active: true }
      ]" />
    </div>
    <ts-card>
      <ts-card-wrapper>
        <form class="form-horizontal">
          <div class="row">
            <div class="col-md-4">
              <fieldset>
                <legend class="text-black">
                  {{
                    $t('school.academic.operation.classOperation.academicStudy')
                  }}
                </legend>
                <div class="form-group row">
                  <div class="col-md-12">
                    <label>
                      {{
                        $t(
                          'school.academic.operation.classOperation.academicYear'
                        )
                      }}
                      <span class="text-danger">*</span>
                      <i class="fa fa-spinner spin" v-if="waitingFetch"></i>
                    </label>
                    <ts-search-select v-model.number="model.ac_year_id" :options="yearLists" @change="() =>
                      (model.ac_term_id = model.acp_id = model.acps_id = null)
                      "></ts-search-select>
                    <div class="validate" v-if="errors.has('ac_year_id')">
                      {{ errors.first('ac_year_id') }}
                    </div>
                  </div>
                </div>
                <div class="form-group row">
                  <div class="col-md-12">
                    <label>
                      {{
                        $t(
                          'school.academic.operation.classOperation.academicTerm'
                        )
                      }}
                      <span class="text-danger">*</span>
                      <i class="fa fa-spinner spin" v-if="waitingFetch"></i>
                    </label>
                    <ts-search-select v-model.number="model.ac_term_id" :options="termLists"
                      :disabled="isDisabled"></ts-search-select>
                    <div class="validate" v-if="errors.has('ac_term_id')">
                      {{ errors.first('ac_term_id') }}
                    </div>
                  </div>
                </div>
                <div class="form-group row">
                  <div class="col-md-12">
                    <label>
                      {{
                        $t(
                          'school.academic.operation.classOperation.mainProgram'
                        )
                      }}
                      <i class="fa fa-spinner spin" v-if="filterSub"></i>
                    </label>
                    <select class="form-control" disabled v-model="model.acp_id">
                      <option v-for="(main, index) in mainProgram" :key="index" :value="main.value">
                        {{ main.text }}
                      </option>
                    </select>
                  </div>
                </div>
                <div class="form-group row">
                  <div class="col-md-12">
                    <label>
                      {{
                        $t(
                          'school.academic.operation.classOperation.subProgram'
                        )
                      }}
                      <i class="fa fa-spinner spin" v-if="filterSub"></i>
                    </label>
                    <select class="form-control" disabled v-model="model.acps_id">
                      <option v-for="(sub, index) in subProgram" :key="index" :value="sub.value">
                        {{ sub.text }}
                      </option>
                    </select>
                  </div>
                </div>
                <div class="form-group row">
                  <div class="col-md-12">
                    <label>
                      {{ $t('school.academic.operation.classOperation.level') }}
                      <i class="fa fa-spinner spin" v-if="filterLevel"></i>
                    </label>
                    <select class="form-control" v-model="model.level_id" :disabled="isDisabled"
                      :class="{ 'is-invalid': errors.has('level_id') }" @change="model.grade_id = ''">
                      <option value>{{ $t('select') }}</option>
                      <option v-for="(level, index) in levelResource" :key="index" :value="level.level_id">
                        {{ level.level_name_foreign }}
                      </option>
                    </select>
                    <div class="invalid-feedback" v-if="errors.has('level_id')">
                      {{ errors.first('level_id') }}
                    </div>
                  </div>
                </div>
                <div class="form-group row">
                  <div class="col-md-12">
                    <label>
                      {{ $t('school.academic.operation.classOperation.grade') }}
                      <span class="text-danger">*</span>
                    </label>
                    <select class="form-control" v-model="model.grade_id" :disabled="isDisabled"
                      :class="{ 'is-invalid': errors.has('grade_id') }" @change="onChangeGrade($event)">
                      <option value>{{ $t('select') }}</option>
                      <option v-for="(grade, index) in grades" :key="index" :value="grade.grade_id">
                        {{ grade.grade_name_foreign }}
                      </option>
                    </select>
                    <div class="invalid-feedback" v-if="errors.has('grade_id')">
                      {{ errors.first('grade_id') }}
                    </div>
                  </div>
                </div>
                <div class="form-group row">
                  <div class="col-md-12">
                    <label>
                      {{
                        $t('school.academic.operation.classOperation.className')
                      }}
                      <span class="text-danger">*</span>
                    </label>
                    <input v-model.trim="model.class_name" type="text" class="form-control"
                      :class="{ 'is-invalid': errors.has('class_name') }" />
                    <div class="invalid-feedback" v-if="errors.has('class_name')">
                      {{ errors.first('class_name') }}
                    </div>
                  </div>
                </div>
                <div class="row mb-2">
                  <div class="col-md-12">
                    <label>
                      {{
                        $t(
                          'school.academic.operation.classOperation.description'
                        )
                      }}
                    </label>
                    <textarea v-model="model.class_desc" class="form-control"
                      :class="{ 'is-invalid': errors.has('class_desc') }" rows="3"></textarea>
                    <div class="invalid-feedback" v-if="errors.has('class_desc')">
                      {{ errors.first('class_desc') }}
                    </div>
                  </div>
                </div>
              </fieldset>
            </div>
            <div class="col-md-8">
              <fieldset>
                <legend class="text-black">
                  {{ $t('school.academic.operation.classOperation.teacher') }}
                </legend>
                <div class="row">
                  <div class="col-md-12">
                    <div class="row">
                      <div class="col-md-6">
                        <div class="form-group row">
                          <label class="col-md-4 col-form-label required">
                            {{
                              $t(
                                'school.academic.operation.classOperation.teacherResponse'
                              )
                            }}
                            <i class="fa fa-spinner spin" v-if="waitingFetch"></i>
                          </label>
                          <div class="col-md-8">
                            <a-select class="ts-w-full" show-search option-filter-prop="children" allowClear
                              :placeholder="$t('select')" v-model="model.teacher_resp_id" :class="{
                                'is-invalid': errors.has('teacher_resp_id')
                              }" :options="teacherLists">
                            </a-select>
                            <div class="validate" v-if="errors.has('teacher_resp_id')">
                              {{ errors.first('teacher_resp_id') }}
                            </div>
                          </div>
                        </div>
                        <div class="form-group row">
                          <label class="col-md-4 col-form-label">{{
                            $t(
                              'school.academic.operation.classOperation.teacherAssistant1'
                            )
                          }}</label>
                          <div class="col-md-8">
                            <a-select class="ts-w-full" show-search option-filter-prop="children" allowClear
                              :placeholder="$t('select')" v-model="model.teacher_asst_id" :class="{
                                'is-invalid': errors.has('teacher_asst_id')
                              }" :options="teacherLists">
                            </a-select>
                            <div class="validate" v-if="errors.has('teacher_asst_id')">
                              {{ errors.first('teacher_asst_id') }}
                            </div>
                          </div>
                        </div>
                        <div class="form-group row">
                          <label class="col-md-4 col-form-label">{{
                            $t(
                              'school.academic.operation.classOperation.teacherAssistant2'
                            )
                          }}</label>
                          <div class="col-md-8">
                            <a-select class="ts-w-full" show-search option-filter-prop="children" allowClear
                              :placeholder="$t('select')" v-model="model.teacher_asst_id2" :class="{
                                'is-invalid': errors.has('teacher_asst_id2')
                              }" :options="teacherLists">
                            </a-select>
                            <div class="validate" v-if="errors.has('teacher_asst_id2')">
                              {{ errors.first('teacher_asst_id2') }}
                            </div>
                          </div>
                        </div>
                      </div>
                      <div class="col-md-6">
                        <div class="form-group row">
                          <label class="col-md-4 col-form-label">{{
                            $t(
                              'school.academic.operation.classOperation.teacherAssistant3'
                            )
                          }}</label>
                          <div class="col-md-8">
                            <a-select class="ts-w-full" show-search option-filter-prop="children" allowClear
                              :placeholder="$t('select')" v-model="model.teacher_asst_id3" :class="{
                                'is-invalid': errors.has('teacher_asst_id3')
                              }" :options="teacherLists">
                            </a-select>
                            <div class="validate" v-if="errors.has('teacher_asst_id3')">
                              {{ errors.first('teacher_asst_id3') }}
                            </div>
                          </div>
                        </div>
                        <div class="form-group row">
                          <label class="col-md-4 col-form-label">{{
                            $t(
                              'school.academic.operation.classOperation.teacherAssistant4'
                            )
                          }}</label>
                          <div class="col-md-8">
                            <a-select class="ts-w-full" show-search option-filter-prop="children" allowClear
                              :placeholder="$t('select')" v-model="model.teacher_asst_id4" :class="{
                                'is-invalid': errors.has('teacher_asst_id4')
                              }" :options="teacherLists">
                            </a-select>
                            <div class="validate" v-if="errors.has('teacher_asst_id4')">
                              {{ errors.first('teacher_asst_id4') }}
                            </div>
                          </div>
                        </div>
                        <div class="form-group row">
                          <label class="col-md-4 col-form-label">{{
                            $t(
                              'school.academic.operation.classOperation.teacherAssistant5'
                            )
                          }}</label>
                          <div class="col-md-8">
                            <a-select class="ts-w-full" show-search option-filter-prop="children" allowClear
                              :placeholder="$t('select')" v-model="model.teacher_asst_id5" :class="{
                                'is-invalid': errors.has('teacher_asst_id5')
                              }" :options="teacherLists">
                            </a-select>
                            <div class="validate" v-if="errors.has('teacher_asst_id5')">
                              {{ errors.first('teacher_asst_id5') }}
                            </div>
                          </div>
                        </div>
                        <div class="form-group row">
                          <label class="col-md-4 col-form-label">{{
                            $t(
                              'school.academic.operation.classOperation.handOverToTeacher'
                            )
                          }}</label>
                          <div class="col-md-8">
                            <a-select class="ts-w-full" show-search option-filter-prop="children" allowClear
                              :placeholder="$t('select')" v-model="model.hand_over_teacher_id" :class="{
                                'is-invalid': errors.has('hand_over_teacher_id')
                              }" :options="teacherLists">
                            </a-select>
                            <div class="validate" v-if="errors.has('hand_over_teacher_id')">
                              {{ errors.first('hand_over_teacher_id') }}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </fieldset>
              <fieldset class="mt-3">
                <legend class="text-black">
                  {{ $t('school.academic.operation.classOperation.moreInfo') }}
                </legend>
                <div class="row">
                  <div class="col-md-12">
                    <div class="row">
                      <div class="col-md-6">
                        <div class="form-group row">
                          <label class="col-md-4 col-form-label required">
                            {{
                              $t(
                                'school.academic.operation.classOperation.building'
                              )
                            }}
                            <i class="fa fa-spinner spin" v-if="waitingFetch"></i>
                          </label>
                          <div class="col-md-8">
                            <select class="form-control" v-model="model.building_id" @change="model.room_id = ''">
                              <option value>{{ $t('select') }}</option>
                              <option v-for="(b, index) in building" :key="index" :value="b.building_id">
                                {{ b.building_name }}
                              </option>
                            </select>
                          </div>
                        </div>
                        <div class="form-group row">
                          <label class="col-md-4 col-form-label required">
                            {{
                              $t(
                                'school.academic.operation.classOperation.studyTime'
                              )
                            }}
                            <i class="fa fa-spinner spin" v-if="waitingFetch"></i>
                          </label>
                          <div class="col-md-8">
                            <select class="form-control" :class="{ 'is-invalid': errors.has('ac_st_id') }"
                              v-model="model.ac_st_id" @change="onChangeStudyTime($event)">
                              <option value>{{ $t('select') }}</option>
                              <option v-for="(time, index) in studyTimes" :key="index" :value="time.ac_st_id">
                                {{ time.study_time }}
                              </option>
                            </select>
                            <div class="invalid-feedback" v-if="errors.has('ac_st_id')">
                              {{ errors.first('ac_st_id') }}
                            </div>
                          </div>
                        </div>
                        <div class="form-group row">
                          <label class="col-md-4 col-form-label">{{
                            $t(
                              'school.academic.operation.classOperation.failClass'
                            )
                          }}</label>
                          <div class="col-md-8">
                            <ts-search-select v-model="model.reference_student_fail_class_id" :options="classLists"
                              :class="{
                                'is-invalid': errors.has(
                                  'reference_student_fail_class_id'
                                )
                              }"></ts-search-select>
                            <div class="invalid-feedback" v-if="errors.has('reference_student_fail_class_id')
                              ">
                              {{
                                errors.first('reference_student_fail_class_id')
                              }}
                            </div>
                          </div>
                        </div>
                        <div class="form-group row">
                          <label class="col-md-4 col-form-label"></label>
                          <div class="col-md-8">
                            <ts-checkbox v-model="model.allow_teacher_pickup">
                              {{
                                $t(
                                  'school.academic.operation.classOperation.allowTeacherPickup'
                                )
                              }}
                              <br />(
                              <small>
                                <i>{{
                                  $t(
                                    'school.academic.operation.classOperation.khmerSeconaryProgramOnly'
                                  )
                                }}</i>
                              </small>
                              )
                            </ts-checkbox>
                          </div>
                        </div>
                      </div>
                      <div class="col-md-6">
                        <div class="form-group row">
                          <label class="col-md-4 col-form-label required">{{
                            $t('school.academic.operation.classOperation.room')
                          }}</label>
                          <div class="col-md-8">
                            <select class="form-control" :class="{ 'is-invalid': errors.has('room_id') }"
                              v-model="model.room_id">
                              <option value>{{ $t('select') }}</option>
                              <option v-for="(room, index) in getRooms" :key="index" :value="room.room_id">
                                {{ room.room_name }}
                              </option>
                            </select>
                            <div class="invalid-feedback" v-if="errors.has('room_id')">
                              {{ errors.first('room_id') }}
                            </div>
                          </div>
                        </div>
                        <div class="form-group row">
                          <label class="col-md-4 col-form-label required">
                            {{
                              $t(
                                'school.academic.operation.classOperation.category'
                              )
                            }}
                            <i class="fa fa-spinner spin" v-if="waitingFetch"></i>
                          </label>
                          <div class="col-md-8">
                            <select class="form-control" v-model.number="model.ac_category_id" :class="{
                              'is-invalid': errors.has('ac_category_id')
                            }">
                              <option value="">{{ $t('select') }}</option>
                              <option v-for="(cat, index) in categories" :key="index" :value="cat.ac_category_id">
                                {{
                                  cat.ac_category_name_native +
                                  '|' +
                                  cat.ac_category_name
                                }}
                              </option>
                            </select>
                            <div class="invalid-feedback" v-if="errors.has('ac_category_id')">
                              {{ errors.first('ac_category_id') }}
                            </div>
                          </div>
                        </div>
                        <div class="form-group row">
                          <label class="col-md-4 col-form-label">{{
                            $t(
                              'school.academic.operation.classOperation.passClass'
                            )
                          }}</label>
                          <div class="col-md-8">
                            <ts-search-select v-model="model.reference_student_pass_class_id" :options="classLists"
                              :class="{
                                'is-invalid': errors.has(
                                  'reference_student_pass_class_id'
                                )
                              }"></ts-search-select>
                            <div class="invalid-feedback" v-if="errors.has('reference_student_pass_class_id')
                              ">
                              {{
                                errors.first('reference_student_pass_class_id')
                              }}
                            </div>
                          </div>
                        </div>
                        <div class="form-group row">
                          <label class="col-md-4 col-form-label">
                            {{
                              $t(
                                'school.academic.operation.classOperation.swapClass'
                              )
                            }}
                            <i class="fa fa-spinner spin" v-if="swapClass"></i>
                          </label>
                          <div class="col-md-8">
                            <select v-model="model.swap_class_id" class="form-control" :class="{
                              'is-invalid': errors.has('swap_class_id')
                            }">
                              <option value="">{{ $t('select') }}</option>
                              <option v-for="(sw, index) in swapClassResource" :key="index" :value="sw.class_id">
                                {{ sw.class_name }}
                              </option>
                            </select>
                            <div class="invalid-feedback" v-if="errors.has('class_id')">
                              {{ errors.first('class_id') }}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </fieldset>
            </div>
          </div>
          <div class="row mt-3">
            <div class="col-md-12 text-right">
              <ts-button color="info" outline class="btn btn-default mr-2"
                @click="$router.push({ name: 'class-operation' })">{{ $t('cancel') }}</ts-button>
              <ts-button :waiting="waiting" class="btn btn-primary" @click.prevent="update">{{ $t('update') }}</ts-button>
            </div>
          </div>
        </form>
      </ts-card-wrapper>
    </ts-card>
  </div>
</template>

<script>
import { Errors } from 'form-backend-validation'
import { mapState } from 'vuex'

export default {
  name: 'classOperationEdit',
  data() {
    return {
      model: {
        branch_id: null,
        class_id: '',
        acp_id: '',
        acps_id: '',
        level_id: '',
        ac_year_id: '',
        ac_term_id: '',
        grade_id: '',
        room_id: '',
        teacher_resp_id: '',
        teacher_asst_id: '',
        teacher_asst_id2: '',
        teacher_asst_id3: '',
        teacher_asst_id4: '',
        teacher_asst_id5: '',
        hand_over_teacher_id: '',
        class_name: '',
        class_desc: '',
        allow_teacher_pickup: false,
        reference_student_fail_class_id: '',
        reference_student_pass_class_id: '',
        ac_category_id: '',
        ac_st_id: '',
        swap_class_id: '',
        building_id: ''
      },
      waiting: false,
      waitingFetch: false,
      filterSub: false,
      filterLevel: false,
      fetTeacherList: false,
      errors: new Errors(),
      subResource: [],
      levelResource: [],
      swapClass: false,
      swapClassResource: []
    }
  },
  created() {
    this.fetchBeforeCreate()
  },
  computed: {
    ...mapState('classOperation', [
      'building',
      'studyTimes',
      'categories',
      'editData'
    ]),
    isDisabled() {
      return parseInt(this.editData.students_count) > 0
    },
    classLists() {
      return this.$store.state.classOperation.classLists.map(el => ({
        value: el.class_id,
        text:
          el.class_name +
          ' (' +
          el.academic_term.term_name +
          '|' +
          el.academic_year.ac_year +
          ')'
      }))
    },
    teacherLists() {
      return this.$store.state.classOperation.teacherList.map(el => ({
        value: el.teacher_id,
        label: el.lname_foreign + ' ' + el.fname_foreign
      }))
    },
    yearLists() {
      return _.sortBy(this.$store.state.classOperation.yearList, [
        'ac_status_id'
      ]).map(el => ({
        value: el.ac_year_id,
        text:
          el.ac_year +
          (el.ac_status_id == 1
            ? ' ( Current ) '
            : el.ac_status_id == 2
              ? ' ( History ) '
              : ' ( Next ) ')
      }))
    },
    termLists() {
      if (this.model.ac_year_id) {
        return _.sortBy(
          this.$store.state.classOperation.termList.filter(
            el => el.ac_year_id == this.model.ac_year_id
          ),
          ['ac_status_id']
        ).map(el => ({
          value: el.ac_term_id,
          text:
            el.term_name +
            (el.ac_status_id == 1
              ? ' ( Current ) '
              : el.ac_status_id == 2
                ? ' ( History ) '
                : ' ( Next ) ')
        }))
      }
      return _.sortBy(this.$store.state.classOperation.termList, [
        'ac_status_id'
      ]).map(el => ({
        value: el.ac_term_id,
        text:
          el.term_name +
          (el.ac_status_id == 1
            ? ' ( Current ) '
            : el.ac_status_id == 2
              ? ' ( History ) '
              : ' ( Next ) ')
      }))
    },
    subProgram: {
      get() {
        return this.subResource.map(el => ({
          value: el.acps_id,
          text: el.acps_name_foreign
        }))
      }
    },
    mainProgram: {
      get() {
        if (this.subResource.length > 0) {
          this.model.acp_id = this.subResource[0].main_program.acp_id
          return [
            {
              value: this.subResource[0].main_program.acp_id,
              text: this.subResource[0].main_program.acp_name_foreign
            }
          ]
        }

        return []
      }
    },
    grades() {
      if (this.levelResource.length > 0) {
        let level = this.levelResource.filter(
          l => l.level_id === this.model.level_id
        )
        if (level.length > 0) {
          return level[0].grades
        }
      }

      return []
    },
    getRooms() {
      if (this.building.length > 0) {
        let room = this.building.filter(
          r => r.building_id === this.model.building_id
        )
        if (room.length > 0) {
          return room[0].rooms
        }
      }

      return []
    }
  },
  methods: {
    onChangeGrade: function (e) {
      if (e.target.value) {
        this.model.class_name =
          e.target.options[e.target.options.selectedIndex].text
      } else {
        this.model.class_name = ''
      }
    },
    onChangeStudyTime: function (e) {
      if (e.target.value) {
        this.model.class_desc =
          e.target.options[e.target.options.selectedIndex].text
      } else {
        this.model.class_desc = ''
      }
    },
    fetchBeforeCreate() {
      this.waitingFetch = true
      this.$store
        .dispatch('classOperation/dataBeforeCreate')
        .then(response => {
          this.setEditData()
        })
        .catch(error => {
          this.$toasted.error(error.message)
        })
        .finally(() => {
          this.waitingFetch = false
        })
    },
    getMainAndSub() {
      let filterSubInterm = this.$store.state.classOperation.termList.filter(
        t => t.ac_term_id === this.model.ac_term_id
      )
      if (filterSubInterm.length) {
        this.filterSub = true
        this.model.ac_year_id = filterSubInterm[0].ac_year_id
        this.model.acps_id = filterSubInterm[0].acps_id
        this.$store
          .dispatch('classOperation/getMainAndSub', filterSubInterm[0].acps_id)
          .then(response => {
            this.subResource = response.data
          })
          .catch(error => {
            this.$toasted.error(error.message)
          })
          .finally(() => {
            this.filterSub = false
          })
      }
    },
    getLevelBySub(acps_id) {
      this.filterLevel = true
      this.$store
        .dispatch('classOperation/getLevel', acps_id)
        .then(response => {
          this.levelResource = response.data
        })
        .catch(error => {
          this.$toasted.error(error.message)
        })
        .finally(() => {
          this.filterLevel = false
        })
    },
    getSwapClass(acps_id) {
      this.swapClass = true
      this.$store
        .dispatch('classOperation/getClassAllowSwap', acps_id)
        .then(response => {
          this.swapClassResource = response.data
        })
        .catch(error => {
          this.$toasted.error(error.message)
        })
        .finally(() => {
          this.swapClass = false
        })
    },
    update() {
      this.errors = new Errors()
      this.waiting = true
      this.$store
        .dispatch('classOperation/update', {
          id: this.editData.class_id,
          data: this.model
        })
        .then(response => {
          this.waiting = false
          this.$toasted.success(response.message)
          this.$router.push({ name: 'class-operation' })
        })
        .catch(error => {
          this.waiting = false
          this.errors = new Errors(error.errors)
          this.$toasted.error(error.message)
        })
    },
    setEditData() {
      this.model = {
        branch_id: this.editData.branch_id,
        class_id: this.editData.class_id,
        acp_id: this.editData.acp_id,
        acps_id: this.editData.acps_id,
        level_id: this.editData.grade.level_id,
        ac_year_id: this.editData.ac_year_id,
        ac_term_id: this.editData.ac_term_id,
        grade_id: this.editData.grade_id,
        room_id: this.editData.room_id,
        teacher_resp_id: this.editData.teacher_resp_id,
        teacher_asst_id: this.editData.teacher_asst_id,
        teacher_asst_id2: this.editData.teacher_asst_id2,
        teacher_asst_id3: this.editData.teacher_asst_id3,
        teacher_asst_id4: this.editData.teacher_asst_id4,
        teacher_asst_id5: this.editData.teacher_asst_id5,
        hand_over_teacher_id: this.editData.hand_over_teacher_id,
        class_name: this.editData.class_name,
        class_desc: this.editData.class_desc,
        allow_teacher_pickup: this.editData.allow_teacher_pickup,
        reference_student_fail_class_id: this.editData
          .reference_student_fail_class_id,
        reference_student_pass_class_id: this.editData
          .reference_student_pass_class_id,
        ac_category_id: this.editData.ac_category_id,
        ac_st_id: this.editData.ac_st_id,
        swap_class_id: this.editData.swap_class_id
          ? this.editData.swap_class_id
          : '',
        building_id: this.editData.room ? parseInt(this.editData.room.building_id) : undefined
      }
      this.getMainAndSub()
    }
  },
  watch: {
    'model.acps_id': function (value) {
      if (value) {
        this.getLevelBySub(value), this.getSwapClass(value)
      }
    },
    'model.ac_term_id': function (value) {
      if (value) {
        this.getMainAndSub()
      }
    }
  },
  beforeRouteLeave(to, from, next) {
    if (_.isEmpty(to.params)) {
      this.$store.commit('classOperation/RESET_STATE')
      this.$store.commit('classOperation/SET_ALL_YEAR_FETCH', false)
    }
    next()
  },
}
</script>
